import React, { useState, useRef, useEffect, useContext } from "react";
import CloseIcon from "../../../components/icons/close";
import Button from "../../../components/button/button";
import Overlay from "../../../components/overlay/overlay";
import Input from "../../../components/input/input";
import List from "../../../components/list/list";
import Loading from "../../../components/loading/loading";
import { useToast } from "../../inmaster/hooks/useToast";
import "./batch-action-modal.css";
import { BackendContext, backendStatus } from "../../../backend/backend";
import BatchActionResultsModal from "./BatchActionResultsModal";

const DISABLE_MAC_VALIDATION =
  process.env.REACT_APP_DISABLE_MAC_VALIDATION === "true";

const DeviceCategory = {
  ROUTER: 0,
  GATEWAY: 1,
};

export const BatchAction = {
  ASSOCIATE: 0,
  DISASSOCIATE: 1,
  UPDATE: 2,
  PROFILE_UPDATE: 3,
};

const BatchActionTitle = {
  [BatchAction.ASSOCIATE]: "Associar em lote",
  [BatchAction.DISASSOCIATE]: "Desassociar em lote",
  [BatchAction.UPDATE]: "Atualizar em lote",
  [BatchAction.PROFILE_UPDATE]: "Atualizar perfis em lote",
};

const BatchActionText = {
  [BatchAction.ASSOCIATE]: "Associar",
  [BatchAction.DISASSOCIATE]: "Desassociar",
  [BatchAction.UPDATE]: "Atualizar",
  [BatchAction.PROFILE_UPDATE]: "Atualizar perfis",
};

const BatchActionInfoText = {
  [BatchAction.ASSOCIATE]:
    "Para associar os dispositivos, coloque as credenciais de acesso aos dispositivos e seus identificadores conforme indicado. Você pode realizar a associação para todos os dispositivos compatíveis que desejar.",
  [BatchAction.DISASSOCIATE]:
    "Para desassociar os dispositivos, coloque o identificador dos dispositivos conforme indicado. Você pode realizar a desassociação para todos os dispositivos compatíveis que desejar.",
  [BatchAction.UPDATE]:
    "Para atualizar os dispositivos, coloque o identificador dos dispositivos conforme indicado. Você pode realizar a atualização para todos os dispositivos compatíveis que desejar.",
  [BatchAction.PROFILE_UPDATE]:
    "Para atualizar os perfis dos dispositivos para o perfil padrão, coloque os identificadores dos dispositivos conforme indicado. Você pode realizar a atualização para todos os dispositivos compatíveis que desejar.",
};

const BatchActionCSV = {
  [BatchAction.ASSOCIATE]:
    "data:text/csv;base64,VVNVQVJJTyxTRU5IQSxNQUMKYWRtaW4sYWRtaW4sMDA6MDA6MDA6MDA6MDA6MDAK",
  [BatchAction.DISASSOCIATE]:
    "data:text/csv;base64,TUFDCjAwOjAwOjAwOjAwOjAwOjAwCg==",
  [BatchAction.UPDATE]:
    "data:text/csv;base64,TUFDLFZFUlNBTwowMDowMDowMDowMDowMDowMCwxLjAK",
  [BatchAction.PROFILE_UPDATE]:
    "data:text/csv;base64,TUFDCjAwOjAwOjAwOjAwOjAwOjAwCg==",
};

export default function BatchActionModal({
  onSubmit,
  batchAction = null,
  disabled = false,
  loading = true,
  selecting = false,
  setSelecting,
  profile = "",
}) {
  const backend = useContext(BackendContext);
  const { addToast } = useToast();
  const [maclist, setMacList] = useState([]);
  const [mac, setMac] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [version, setVersion] = useState("");
  const [clientid, setClientid] = useState("");
  const [category, setCategory] = useState(DeviceCategory.ROUTER);
  const [devices, setDevices] = useState([]);
  const [model, setModel] = useState("");
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("Selecione um arquivo.");
  const [csvData, setCsvData] = useState(new Map());
  const [csvWasLoaded, setCsvWasLoaded] = useState(false);
  const csvByBatchAction = BatchActionCSV[batchAction];
  const isBatchAction = batchAction !== null;
  const title = isBatchAction
    ? BatchActionTitle[batchAction]
    : "Associar dispositivos";

  useEffect(() => {
    if (csvWasLoaded) {
      if (maclist.length === 0) {
        resetState();
      }
    }
  }, [maclist]);

  useEffect(() => {
    const macFormatted = mac.replace(/:/g, "").toLowerCase();
    const clearUpdate = () => {
      setModel("");
      setVersion("");
    };
    const searchDevice = async () => {
      let result = await backend.retrieveFresh(`devices/${macFormatted}`);
      if (result.status === backendStatus.SUCCESS) {
        setModel(result.content.model);
        setVersion(result.content.fw_version);
      } else {
        clearUpdate();
        addToast("error", "Dispositivo não encontrado!");
      }
    };

    if (batchAction === BatchAction.UPDATE) {
      if (macFormatted.length === 12) {
        searchDevice();
      } else {
        clearUpdate();
      }
    }
  }, [mac]);

  const openFile = () => {
    inputRef.current.click();
  };

  const blobToData = (blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsText(blob);
    });
  };

  const handleOpenFile = async (event) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) {
      return;
    }

    resetState();

    const response = await blobToData(fileObj);
    const separator = response.includes(";") ? ";" : ",";

    let lines = response
      .split("\n")
      .filter((e) => e.length !== 0)
      .map(function (line) {
        let res = line.split(separator).map((e) => e.replace("\r", ""));
        return res.filter((e) => e.length !== 0);
      });

    let csv = new Map();
    const isAssociate = batchAction === BatchAction.ASSOCIATE;
    const isUpdate = batchAction === BatchAction.UPDATE;
    try {
      for (let i = 0; i < lines.length; i++) {
        let newDevice = {};
        if (i === 0) {
          let isCsvValid = false;

          switch (batchAction) {
            case BatchAction.ASSOCIATE:
              isCsvValid =
                lines[i][0] === "USUARIO" &&
                lines[i][1] === "SENHA" &&
                lines[i][2] === "MAC";
              break;
            case BatchAction.DISASSOCIATE:
              isCsvValid = lines[i][0] === "MAC";
              break;
            case BatchAction.PROFILE_UPDATE:
              isCsvValid = lines[i][0] === "MAC";
              break;
            case BatchAction.UPDATE:
              isCsvValid = lines[i][0] === "MAC" && lines[i][1] === "VERSAO";
              break;
            default:
              isCsvValid = false;
          }

          if (isCsvValid) {
            continue;
          }
          addToast(
            "error",
            'Erro no cabeçalho do arquivo. Obtenha o padrão correto clicando em "Baixar modelo".'
          );

          resetState();
          return;
        }
        if (lines[i][3] === undefined) {
          lines[i].push("ND");
        }
        const macCell = isAssociate ? lines[i][2] : lines[i][0];
        const macKey = macCell.replace(/:/g, "").toLowerCase().trim();

        if (isUpdate) {
          newDevice = {
            version: lines[i][1].trim(),
          };
        }

        if (isAssociate) {
          newDevice = {
            username: lines[i][0].trim(),
            password: lines[i][1].trim(),
          };

          csv.set(macKey, {
            username: lines[i][0].length !== 0 ? lines[i][0].trim() : "admin",
            password: lines[i][1].length !== 0 ? lines[i][1].trim() : "admin",
            devices: [],
            clientid: lines[i][3].length !== 0 ? lines[i][3].trim() : "ND",
            category: category,
          });
        } else {
          csv.set(macKey, {
            devices: [],
            category: category,
          });
        }

        setDevices((prev) => [
          ...prev,
          {
            ...newDevice,
            deviceid: macKey,
          },
        ]);
      }
    } catch (error) {
      resetState();
      return;
    }

    try {
      for (let i = 0; i < lines.length; i++) {
        if (i === 0) {
          continue;
        }
        const macCell = isAssociate ? lines[i][2] : lines[i][0];
        const mac = macCell.replace(/:/g, "").toLowerCase().trim();

        if (validadeMac(mac) === "") {
          csv.get(mac).devices.push(mac);
        } else {
          addToast(
            "error",
            "Um ou mais endereços MAC presentes no arquivo estão incorretos."
          );
        }
      }
    } catch (error) {
      resetState();
      return;
    }

    csv.forEach((element) => {
      element.devices.forEach((mac) => {
        addMacFromCsv(mac);
      });
    });

    setCsvData(csv);
    setFileName(fileObj.name);
    setCsvWasLoaded(true);
  };

  const addMacFromCsv = (mac) => {
    const standardizedMac = mac.replace(/:/g, "").toLowerCase();
    if (!maclist.every((item) => item[0].props.children[0] !== mac)) return;

    maclist.push([
      <div className="mac-entry">
        {standardizedMac}

        <div
          className="mac-entry del-icon"
          onClick={() => {
            setMacList((prev) => [
              ...prev.filter((entry) => {
                return entry[0].props.children[0] !== standardizedMac;
              }),
            ]);

            setCsvData((prev) => {
              prev.forEach((value, key) => {
                value.devices = value.devices.filter(
                  (item) => item !== standardizedMac
                );
                if (value.devices.length === 0) {
                  prev.delete(key);
                }
              });
              return prev;
            });
          }}
        >
          <CloseIcon size={12}></CloseIcon>
        </div>
      </div>,
    ]);

    setMacList([...maclist]);
    setMac("");
  };

  const addMac = () => {
    const standardizedMac = mac.replace(/:/g, "").toLowerCase();
    setDevices((prev) => [
      ...prev,
      {
        deviceid: standardizedMac,
        username: username,
        password: password,
        version: version,
      },
    ]);
    maclist.push([
      <div className="mac-entry">
        {standardizedMac}

        <div
          className="mac-entry del-icon"
          onClick={() => {
            console.log("devices", devices);

            setMacList((prev) => [
              ...prev.filter(
                (entry) => entry[0].props.children[0] !== standardizedMac
              ),
            ]);
          }}
        >
          <CloseIcon size={12}></CloseIcon>
        </div>
      </div>,
    ]);

    setMacList([...maclist]);
    setMac("");
    setVersion("");
  };

  const submitAssociation = async () => {
    const response = await onSubmit({
      username: username,
      password: password,
      clientid: clientid,
      devices: devices,
      category: category,
    });
    if (response && response.content) {
      setResults(response.content);
      setShowResults(true);
    }

    resetState();
  };

  const submitAssociationCsv = async () => {
    const response = await onSubmit({
      devices: devices,
    });

    if (response && response.content) {
      setResults(response.content);
      setShowResults(true);
    }

    resetState();
  };

  const resetState = () => {
    setUsername("");
    setPassword("");
    setClientid("");
    setVersion("");
    setMacList(maclist.splice(0, maclist.length));
    setMacList([]);
    setDevices([]);
    setCsvWasLoaded(false);
    csvData.clear();
    setFileName("Selecione um arquivo.");
    if (inputRef.current !== null) {
      inputRef.current.value = "";
    }
  };

  const closeResultsModal = () => {
    setShowResults(false);
    setResults(null);
    setSelecting(false);
  };

  const submitDisabled = () => {
    if (batchAction === BatchAction.ASSOCIATE) {
      return username === "" || password === "" || maclist.length === 0;
    }
    return maclist.length === 0;
  };

  const validadeMac = (value) => {
    if (category !== DeviceCategory.ROUTER) return "";

    if (value === "") {
      return "Não pode ser vazio";
    }

    if (DISABLE_MAC_VALIDATION) return "";

    let stripedMac = value.replace(/:/g, "").toLowerCase();
    let regex = /[0-9a-f]{12}/;

    if (stripedMac.length !== 12 || !regex.test(stripedMac)) {
      return "Mac inválido";
    }

    for (let mac of maclist) {
      let macValue = mac[0].props.children[0];
      if (macValue === value) return "Este dispositivo já foi adicionado";
    }

    return "";
  };

  const shouldDisableAddMac = () => {
    if (batchAction === BatchAction.UPDATE) {
      return model.length === 0;
    }
    return false;
  };

  return (
    <div id="device-associator">
      <Button
        className="itb-button"
        text={title}
        onClick={() => setSelecting(true)}
        disabled={disabled}
      ></Button>

      <Overlay open={selecting} onClick={() => setSelecting(false)}></Overlay>

      {selecting && (
        <>
          {loading ? (
            <div id="device-associator-container" className="loading-wrapper">
              <Loading show={loading} />
            </div>
          ) : (
            <div id="device-associator-container">
              <div
                className="close"
                onClick={() => {
                  setSelecting(false);
                  resetState();
                }}
              >
                <CloseIcon size={15}></CloseIcon>
              </div>

              <div className="device-associator-form">
                <div className="subtitle">{title}</div>

                {category === DeviceCategory.GATEWAY && (
                  <div className="info-text warning">
                    Acesse o guia de instalação e faça o download{" "}
                    <a href="https://remotize.intelbras.com.br/manual/#51">
                      clicando aqui!
                    </a>
                  </div>
                )}

                <div className="info-text">
                  {batchAction
                    ? BatchActionInfoText[batchAction]
                    : "Para associar o dispositivo, coloque as credenciais de acesso aos dispositivos e seus identificadores conforme indicado. Você pode realizar a associação para todos os dispositivos compatíveis que desejar."}
                  {batchAction === BatchAction.PROFILE_UPDATE && (
                    <>
                      <br />
                      <br />
                      <span>
                        <b> Perfil padrão:</b> {profile}
                      </span>
                    </>
                  )}
                </div>

                {batchAction === BatchAction.UPDATE && (
                  <>
                    <Input
                      label="MAC"
                      id="mac"
                      placeholder="MAC"
                      value={mac}
                      onChange={(e) => {
                        setMac(e.target.value);
                      }}
                      disabled={csvWasLoaded}
                    ></Input>
                    <Input
                      label="Modelo"
                      id="model"
                      value={model}
                      disabled={true}
                    ></Input>
                    <Input
                      label="Versão"
                      id="version"
                      value={version}
                      disabled={!model.length}
                      onChange={(e) => {
                        setVersion(e.target.value);
                      }}
                    ></Input>
                  </>
                )}

                {batchAction === BatchAction.ASSOCIATE ? (
                  <>
                    {category === DeviceCategory.ROUTER ? (
                      <Input
                        label="Usuário dos roteadores"
                        id="associate-user"
                        placeholder="Usuário"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                        }}
                        disabled={csvWasLoaded}
                      ></Input>
                    ) : null}

                    <Input
                      label={
                        category === DeviceCategory.ROUTER
                          ? "Senha dos roteadores"
                          : "Token de autenticação"
                      }
                      id="associate-password"
                      placeholder="Senha"
                      vaue={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                      }}
                      disabled={csvWasLoaded}
                    ></Input>
                  </>
                ) : null}

                <div className="input-add">
                  {batchAction !== BatchAction.UPDATE && (
                    <Input
                      id="associate-add"
                      label={
                        category === DeviceCategory.ROUTER
                          ? "MAC da LAN"
                          : "Identificador do gateway"
                      }
                      value={mac}
                      onChange={(e) => {
                        setMac(e.target.value);
                      }}
                      validators={[validadeMac]}
                      disabled={csvWasLoaded}
                    ></Input>
                  )}

                  {category === DeviceCategory.ROUTER ? (
                    <Button
                      text={
                        maclist.length === 0 ? "Adicionar" : "Adicionar mais"
                      }
                      onClick={addMac}
                      disabled={
                        validadeMac(mac) !== "" || shouldDisableAddMac()
                      }
                    ></Button>
                  ) : (
                    <Button
                      text={"Associar"}
                      onClick={() => {
                        addMac();
                        submitAssociation();
                      }}
                      disabled={mac.length === 0}
                    ></Button>
                  )}
                </div>

                {category === DeviceCategory.ROUTER ? (
                  <div className="input-add">
                    <div className="open-file">
                      <div className="file-name">
                        <input
                          style={{ display: "none" }}
                          ref={inputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleOpenFile}
                        />

                        <div className="filename-label">{fileName}</div>
                      </div>

                      <div className="import-btn">
                        <Button
                          text={"Importar csv"}
                          onClick={openFile}
                        ></Button>
                      </div>
                    </div>
                  </div>
                ) : null}

                {category === DeviceCategory.ROUTER ? (
                  <div className="download-model">
                    <div>
                      <a
                        className="download-link"
                        href={csvByBatchAction}
                        download="modelo.csv"
                      >
                        Baixar modelo
                      </a>
                    </div>
                  </div>
                ) : null}
              </div>

              {category === DeviceCategory.ROUTER ? (
                <div className="device-associator-list">
                  <div className="assoc-list-container">
                    <List
                      columns={[{ header: "MAC do dispositivo" }]}
                      lines={maclist}
                      width={300}
                    ></List>
                  </div>

                  <Button
                    id="associate-submit"
                    text={`Pronto! ${
                      isBatchAction ? BatchActionText[batchAction] : "Associar"
                    }`}
                    onClick={
                      csvWasLoaded ? submitAssociationCsv : submitAssociation
                    }
                    disabled={submitDisabled() && !csvWasLoaded}
                  ></Button>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

      <BatchActionResultsModal
        open={showResults}
        onClose={closeResultsModal}
        results={results || { success: [], error: [] }}
        title={`Resultados: ${BatchActionTitle[batchAction]}`}
      />
    </div>
  );
}
